import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Save, Languages, RotateCcw, Plus, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { adminAPI } from '../services/api';
import { invalidateHomeContent } from '../services/homeContent';

const LANGS = ['fr', 'en', 'de', 'es', 'it'];
const FLAG = { fr: '🇫🇷 FR', en: '🇬🇧 EN', de: '🇩🇪 DE', es: '🇪🇸 ES', it: '🇮🇹 IT' };

const AboutEditor = ({ lang, value, onChange }) => (
  <div className="space-y-2" data-testid="home-content-about">
    <Input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} placeholder="Titre (H2) du bloc « À propos »" maxLength={140} data-testid="home-content-about-title" />
    <Textarea rows={12} value={value.paragraphs.join('\n\n')} onChange={(e) => onChange({ ...value, paragraphs: e.target.value.split(/\n\s*\n/) })} className="text-sm" placeholder="Un paragraphe par bloc, séparés par une ligne vide. **gras** autorisé." data-testid="home-content-about-text" />
    <p className="text-[11px] text-gray-500">{value.paragraphs.filter((p) => p.trim()).length} paragraphe(s) · séparez-les par une ligne vide · <code>**mot**</code> = gras. Conseil SEO : mots-clés dans le titre et le 1er paragraphe, 250–400 mots.</p>
  </div>
);

const FaqEditor = ({ value, onChange }) => {
  const setItem = (i, patch) => onChange({ ...value, items: value.items.map((it, k) => (k === i ? { ...it, ...patch } : it)) });
  return (
    <div className="space-y-3" data-testid="home-content-faq">
      <Input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} placeholder="Titre (H2) de la FAQ" maxLength={140} data-testid="home-content-faq-title" />
      {value.items.map((it, i) => (
        <div key={i} className="border rounded-lg p-3 bg-gray-50 space-y-2" data-testid={`home-content-faq-item-${i}`}>
          <div className="flex gap-2">
            <Input value={it.question} onChange={(e) => setItem(i, { question: e.target.value })} placeholder={`Question ${i + 1}`} maxLength={200} data-testid={`home-content-faq-q-${i}`} />
            <Button type="button" variant="ghost" size="icon" onClick={() => onChange({ ...value, items: value.items.filter((_, k) => k !== i) })} className="text-red-500 flex-shrink-0" data-testid={`home-content-faq-remove-${i}`}><Trash2 size={15} /></Button>
          </div>
          <Textarea rows={3} value={it.answer} onChange={(e) => setItem(i, { answer: e.target.value })} placeholder="Réponse (phrase complète, 40–80 mots)" maxLength={1200} className="text-sm" data-testid={`home-content-faq-a-${i}`} />
        </div>
      ))}
      {value.items.length < 12 && (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange({ ...value, items: [...value.items, { question: '', answer: '' }] })} data-testid="home-content-faq-add"><Plus size={14} className="mr-1" /> Ajouter une question</Button>
      )}
    </div>
  );
};

// Admin › Paramètres : textes de la page d'accueil (« À propos » + FAQ) par langue, sans toucher au code
export const HomeContentPanel = () => {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState('fr');
  const [busy, setBusy] = useState('');

  useEffect(() => { adminAPI.homeContent().then(setData).catch(() => setData({ error: true })); }, []);
  if (!data || data.error) return null;

  const about = data.about[lang];
  const faq = data.faq[lang];
  const custom = (part) => data.custom[part].includes(lang);
  const update = (part, value) => setData({ ...data, [part]: { ...data[part], [lang]: value } });
  const apply = (res) => { setData(res); invalidateHomeContent(); };

  const save = async () => {
    setBusy('save');
    try {
      apply(await adminAPI.saveHomeContent({ about: { [lang]: about }, faq: { [lang]: faq } }));
      toast.success(`Textes ${lang.toUpperCase()} enregistrés`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); }
    finally { setBusy(''); }
  };
  const reset = async (part) => {
    if (!window.confirm(`Revenir au texte par défaut (${lang.toUpperCase()}) pour ${part === 'about' ? '« À propos »' : 'la FAQ'} ?`)) return;
    setBusy(`reset-${part}`);
    try { apply(await adminAPI.resetHomeContent(lang, part)); toast.success('Texte par défaut restauré'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Réinitialisation impossible'); }
    finally { setBusy(''); }
  };
  const translate = async (all) => {
    if (!window.confirm(all ? 'Traduire les textes FR vers EN, DE, ES et IT (IA) et remplacer les versions existantes ?' : `Traduire les textes FR vers ${lang.toUpperCase()} (IA) et remplacer la version existante ?`)) return;
    setBusy('translate');
    try {
      const res = await adminAPI.translateHomeContent(all ? null : [lang]);
      apply(res);
      toast.success(`Traduit : ${res.translated.map((l) => l.toUpperCase()).join(', ')}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Traduction impossible'); }
    finally { setBusy(''); }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 max-w-3xl mt-6" data-testid="home-content-panel">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2"><FileText size={20} className="text-orange-500" /><h2 className="font-semibold text-gray-900">Textes de la page d'accueil</h2></div>
        <a href="/" target="_blank" rel="noreferrer" className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1" data-testid="home-content-open">Voir l'accueil <ExternalLink size={11} /></a>
      </div>
      <p className="text-sm text-gray-500 mb-4">Bloc « À propos » (bas de page) et questions fréquentes (FAQ avec données structurées Google). Modifiez la langue de votre choix puis enregistrez ; le bouton IA traduit la version française vers les autres langues.</p>
      <div className="flex flex-wrap gap-1 mb-4" role="tablist">
        {LANGS.map((l) => (
          <button key={l} type="button" role="tab" aria-selected={lang === l} onClick={() => setLang(l)} data-testid={`home-content-lang-${l}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${lang === l ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}>
            {FLAG[l]}{(data.custom.about.includes(l) || data.custom.faq.includes(l)) && <span className="ml-1 text-[10px] opacity-80" title="Texte personnalisé">●</span>}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800">Bloc « À propos » {custom('about') ? <span className="text-[10px] font-normal text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5 ml-1" data-testid="home-content-about-custom">personnalisé</span> : <span className="text-[10px] font-normal text-gray-500 ml-1">texte par défaut</span>}</h3>
            {custom('about') && <button type="button" onClick={() => reset('about')} disabled={!!busy} className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" data-testid="home-content-about-reset"><RotateCcw size={12} /> Par défaut</button>}
          </div>
          <AboutEditor lang={lang} value={about} onChange={(v) => update('about', v)} />
        </section>
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800">FAQ {custom('faq') ? <span className="text-[10px] font-normal text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5 ml-1" data-testid="home-content-faq-custom">personnalisée</span> : <span className="text-[10px] font-normal text-gray-500 ml-1">texte par défaut</span>}</h3>
            {custom('faq') && <button type="button" onClick={() => reset('faq')} disabled={!!busy} className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" data-testid="home-content-faq-reset"><RotateCcw size={12} /> Par défaut</button>}
          </div>
          <FaqEditor value={faq} onChange={(v) => update('faq', v)} />
        </section>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-6 pt-4 border-t">
        <div className="flex gap-2">
          {lang !== 'fr' && <Button type="button" variant="outline" size="sm" onClick={() => translate(false)} disabled={!!busy} data-testid="home-content-translate-one">{busy === 'translate' ? <Loader2 size={14} className="animate-spin mr-1" /> : <Languages size={14} className="mr-1" />} Traduire FR → {lang.toUpperCase()} (IA)</Button>}
          <Button type="button" variant="outline" size="sm" onClick={() => translate(true)} disabled={!!busy} data-testid="home-content-translate-all">{busy === 'translate' ? <Loader2 size={14} className="animate-spin mr-1" /> : <Languages size={14} className="mr-1" />} Traduire FR → toutes (IA)</Button>
        </div>
        <Button type="button" onClick={save} disabled={!!busy} className="bg-green-600 hover:bg-green-700" data-testid="home-content-save">{busy === 'save' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Enregistrer {lang.toUpperCase()}</Button>
      </div>
    </div>
  );
};

export default HomeContentPanel;
